import { translatePublicSurfaceString } from "../../i18n/publicSurfaceCatalog";
import type { CountRange, ObservatoryView } from "./schema";
import { formatActiveNumber } from "../../i18n/formatting";
export function countRange(value?: CountRange) {
  return value
    ? `${formatActiveNumber(value.lower)}–${formatActiveNumber(value.upper)}`
    : "—";
}
export function normalize(value: string) {
  return value
    .normalize("NFKC")
    .toLocaleLowerCase()
    .replaceAll("ي", "ی")
    .replaceAll("ك", "ک")
    .trim();
}
export function matches(query: string, values: string[]) {
  const hay = normalize(values.join(" "));
  return normalize(query)
    .split(/\s+/)
    .every((word) => hay.includes(word));
}
export function monthsBetween(first: string, last: string) {
  const result: string[] = [];
  let year = Number(first.slice(0, 4)),
    month = Number(first.slice(5));
  for (let i = 0; i < 1200; i++) {
    const value = `${year}-${String(month).padStart(2, "0")}`;
    if (value > last) break;
    result.push(value);
    if (++month > 12) {
      month = 1;
      year++;
    }
  }
  return result;
}
export function returningPatterns(view: ObservatoryView) {
  const threats = [...new Set(view.series.map((v) => v.threat))];
  return threats.flatMap((threat) => {
    const values = view.series
      .filter((v) => v.threat === threat)
      .sort((a, b) => a.month.localeCompare(b.month));
    if (values.length < 5) return [];
    const last = values.at(-1)!,
      previous = values.at(-2)!;
    const history = monthsBetween(values[0]!.month, last.month);
    // Never infer quiet from a missing/suppressed cell, or treat overlapping ranges as a rise.
    if (history.length !== values.length) return [];
    for (let i = values.length - 5; i >= 0; i--) {
      const peak = values[i]!;
      const lows = values.slice(i + 1, -2);
      if (previous.lower <= 0 || last.lower < peak.upper * 0.8) continue;
      const quiet = lows.some(
        (v, j) =>
          j + 1 < lows.length &&
          v.upper < peak.lower * 0.6 &&
          lows[j + 1]!.upper < peak.lower * 0.6 &&
          last.lower > Math.max(v.upper, lows[j + 1]!.upper) * 1.8,
      );
      if (quiet) return [{ threat, earlier: peak.month, renewed: last.month }];
    }
    return [];
  });
}

const threatSearchAliases: Record<string, string[]> = {
  Phishing: ["فیشینگ"],
  Impersonation: ["جعل هویت"],
  "Compromise Credentials": ["سرقت اطلاعات ورود", "تصاحب حساب"],
  "Suspicious Message": ["پیام مشکوک"],
  Malware: ["بدافزار"],
  Adware: ["تبلیغ‌افزار"],
  "Denial-of-Service": ["منع سرویس", "اختلال سرویس"],
  "Mass Followers": ["فالوور انبوه"],
  Spamming: ["هرزنامه", "اسپم"],
  Scam: ["کلاهبرداری"],
  Doxxing: ["داکسینگ", "افشای اطلاعات شخصی"],
};
export function reportingSearchTerms(
  value: string,
  locale: Parameters<typeof translatePublicSurfaceString>[0],
) {
  if (/^20\d{2}-\d{2}$/.test(value)) return [value];
  return [
    value,
    translatePublicSurfaceString(locale, value),
    translatePublicSurfaceString("fa", value),
    ...(threatSearchAliases[value] ?? []),
  ];
}
